import { bottomLeftOptimize } from './bottomLeft.js';
import { maximalRectanglesOptimize } from './maximalRectangles.js';
import { skylineOptimize } from './skyline.js';
import { ffdhOptimize } from './ffdh.js';
import { maxRectsGlobalOptimize } from './maxRectsGlobal.js';
import { guillotineOptimize, guillotineMINASOptimize, guillotineSLASOptimize } from './guillotine.js';
import { skylineWMOptimize } from './skylineWM.js';
import { simulatedAnnealingOptimize } from './simulatedAnnealing.js';
import { SORT_STRATEGIES, scoreResult, isBetter } from './common.js';

const algorithmDefs = [
  { name: 'Bottom-Left', fn: bottomLeftOptimize },
  { name: 'Skyline', fn: skylineOptimize },
  { name: 'FFDH', fn: ffdhOptimize },
  { name: 'MaxRects', fn: maximalRectanglesOptimize },
  { name: 'MaxRects-Global', fn: maxRectsGlobalOptimize },
  { name: 'Guillotine-SAS', fn: guillotineOptimize },
  { name: 'Guillotine-MINAS', fn: guillotineMINASOptimize },
  { name: 'Guillotine-SLAS', fn: guillotineSLASOptimize },
  { name: 'Skyline-WM', fn: skylineWMOptimize },
];

const sortDefs = [
  { name: 'DESCSS', key: SORT_STRATEGIES.DESCSS },
  { name: 'Area', key: SORT_STRATEGIES.AREA },
  { name: 'Perimeter', key: SORT_STRATEGIES.PERIMETER },
  { name: 'LongSide', key: SORT_STRATEGIES.LONG_SIDE },
  { name: 'Difference', key: SORT_STRATEGIES.DIFFERENCE },
];

/**
 * Best Result algorithm — runs all algorithms × 5 sort strategies (~45 combos),
 * then runs SA as a polishing pass seeded with the best deterministic result.
 * Picks the best by fewest unplaced, then fewest sheets, then highest efficiency.
 */
export const bestResultOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth) => {
  const sheetArea = sheetWidth * sheetHeight;
  let bestResult = null;
  let bestScore = null;
  let bestAlgo = '';
  let bestSort = '';

  // 9 algorithms × 5 sorts = 45 deterministic runs
  outer:
  for (const algo of algorithmDefs) {
    for (const sort of sortDefs) {
      const result = algo.fn(allPieces, sheetWidth, sheetHeight, cutWidth, sort.key);
      const score = scoreResult(result, sheetArea);
      if (!bestScore || isBetter(score, bestScore)) {
        bestScore = score;
        bestResult = result;
        bestAlgo = algo.name;
        bestSort = sort.name;
      }
      // Early exit: perfect packing found (0 unplaced, high efficiency)
      if (bestScore.unplaced === 0 && bestScore.eff > 0.95) break outer;
    }
  }

  // Skip SA polish if deterministic result is already excellent
  const skipSA = bestScore && bestScore.unplaced === 0 &&
    (bestScore.sheets === 1 || bestScore.eff > 0.98);

  if (!skipSA) {
    const saResult = simulatedAnnealingOptimize(allPieces, sheetWidth, sheetHeight, cutWidth, bestResult);
    const saScore = scoreResult(saResult, sheetArea);
    if (isBetter(saScore, bestScore)) {
      bestResult = saResult;
      bestScore = saScore;
      bestAlgo = 'SA';
      bestSort = '';
    }
  }

  if (bestResult) {
    bestResult.algorithmUsed = bestAlgo;
    bestResult.sortStrategyUsed = bestSort;
  }

  return bestResult;
};

/**
 * Async version of bestResultOptimize that uses Web Workers for parallelism.
 * Falls back to synchronous version if Workers are unavailable.
 */
export const bestResultOptimizeAsync = (allPieces, sheetWidth, sheetHeight, cutWidth) => {
  // Check Worker availability
  if (typeof Worker === 'undefined') {
    return Promise.resolve(bestResultOptimize(allPieces, sheetWidth, sheetHeight, cutWidth));
  }

  return new Promise((resolve) => {
    const sheetArea = sheetWidth * sheetHeight;
    let bestResult = null;
    let bestScore = null;
    let bestAlgo = '';
    let bestSort = '';
    let resolved = false;

    // Build job list: 9 algorithms × 5 sorts = 45 jobs
    const jobs = [];
    for (const algo of algorithmDefs) {
      for (const sort of sortDefs) {
        jobs.push({ algorithmName: algo.name, sortStrategy: sort.key, sortName: sort.name });
      }
    }

    const poolSize = Math.min(
      typeof navigator !== 'undefined' && navigator.hardwareConcurrency ? navigator.hardwareConcurrency : 4,
      8,
      jobs.length
    );

    let workers;
    try {
      workers = Array.from({ length: poolSize }, () =>
        new Worker(new URL('./packingWorker.js', import.meta.url))
      );
    } catch (e) {
      // Worker creation failed — fall back to sync
      resolve(bestResultOptimize(allPieces, sheetWidth, sheetHeight, cutWidth));
      return;
    }

    let nextJob = 0;
    let completed = 0;
    const totalJobs = jobs.length;

    function finish() {
      if (resolved) return;
      resolved = true;
      workers.forEach(w => w.terminate());

      // SA polishing pass on main thread (needs warm start from best result)
      const skipSA = bestScore && bestScore.unplaced === 0 &&
        (bestScore.sheets === 1 || bestScore.eff > 0.98);

      if (!skipSA && bestResult) {
        const saResult = simulatedAnnealingOptimize(allPieces, sheetWidth, sheetHeight, cutWidth, bestResult);
        const saScore = scoreResult(saResult, sheetArea);
        if (isBetter(saScore, bestScore)) {
          bestResult = saResult;
          bestAlgo = 'SA';
          bestSort = '';
        }
      }

      if (bestResult) {
        bestResult.algorithmUsed = bestAlgo;
        bestResult.sortStrategyUsed = bestSort;
      }

      resolve(bestResult);
    }

    function dispatch(worker) {
      if (nextJob >= totalJobs || resolved) return;
      const job = jobs[nextJob++];
      worker.postMessage({
        type: 'run',
        jobId: nextJob,
        algorithmName: job.algorithmName,
        sortStrategy: job.sortStrategy,
        pieces: allPieces,
        sheetWidth,
        sheetHeight,
        cutWidth,
      });
    }

    workers.forEach(worker => {
      worker.onmessage = (e) => {
        if (resolved) return;
        const { type, result, algorithmName, sortStrategy } = e.data;
        if (type === 'result' && result) {
          const score = scoreResult(result, sheetArea);
          const sortName = sortDefs.find(s => s.key === sortStrategy)?.name || '';
          if (!bestScore || isBetter(score, bestScore)) {
            bestScore = score;
            bestResult = result;
            bestAlgo = algorithmName;
            bestSort = sortName;
          }
          // Early termination: perfect packing
          if (bestScore.unplaced === 0 && bestScore.eff > 0.95) {
            finish();
            return;
          }
        }
        completed++;
        if (completed >= totalJobs) {
          finish();
        } else {
          dispatch(worker);
        }
      };

      worker.onerror = () => {
        if (resolved) return;
        completed++;
        if (completed >= totalJobs) {
          finish();
        } else {
          dispatch(worker);
        }
      };

      // Start initial dispatch
      dispatch(worker);
    });

    // Safety timeout: if workers take too long, fall back
    setTimeout(() => {
      if (!resolved) {
        finish();
      }
    }, 30000);
  });
};
